import {
  authRoutes,
  userRoutes,
  problemSchema,
  type Problem,
  type RouteDefinition,
} from '@pickpro/contracts';
import type { z, ZodTypeAny } from 'zod';
import { ApiError } from './api-error';
import { apiOrigin } from './api-origin';
import {
  advanceSessionGeneration,
  assertCurrentSession,
  getSessionGeneration,
} from './session-generation';

type SchemaInput<S> = S extends ZodTypeAny ? z.input<S> : never;
type SchemaOutput<S> = S extends ZodTypeAny ? z.output<S> : never;

export type RouteInput<R extends RouteDefinition> = {
  signal?: AbortSignal;
  params?: SchemaInput<R['params']>;
  query?: SchemaInput<R['query']>;
  body?: SchemaInput<R['body']>;
};
export type RouteOutput<R extends RouteDefinition> = SchemaOutput<R['response']>;

const fillPath = (path: string, params: Record<string, unknown> | undefined): string =>
  path.replace(/:([A-Za-z]+)/g, (_match, name: string) =>
    encodeURIComponent(String(params?.[name] ?? '')),
  );

const buildQuery = (query: Record<string, unknown> | undefined): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null) search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : '';
};

const problemFor = (status: number, title: string, messageKey: string): Problem => ({
  type: 'about:blank',
  title,
  status,
  messageKey,
});

type SessionFailureHandler = () => void;
let sessionFailureHandler: SessionFailureHandler | null = null;
let refreshInFlight: Promise<Response> | null = null;
let refreshGeneration = 0;

const withSessionLock = async (operation: () => Promise<Response>): Promise<Response> =>
  await (globalThis.navigator?.locks?.request('pickpro:refresh', operation) ?? operation());

const changesSession = (route: RouteDefinition): boolean =>
  route === authRoutes.login || route === authRoutes.logout || route === userRoutes.deleteAccount;

const deletesAccount = (route: RouteDefinition): boolean => route === userRoutes.deleteAccount;

const rejectSession = (): void => {
  advanceSessionGeneration();
  sessionFailureHandler?.();
};

export const setSessionFailureHandler = (handler: SessionFailureHandler | null): (() => void) => {
  sessionFailureHandler = handler;
  return () => {
    if (sessionFailureHandler === handler) sessionFailureHandler = null;
  };
};

const send = async (
  route: RouteDefinition,
  input: RouteInput<RouteDefinition>,
): Promise<Response> => {
  const url = `${apiOrigin}${fillPath(route.path, input.params as Record<string, unknown> | undefined)}${buildQuery(input.query as Record<string, unknown> | undefined)}`;
  try {
    const response = await fetch(url, {
      method: route.method,
      credentials: 'include',
      signal: input.signal,
      headers: input.body === undefined ? {} : { 'content-type': 'application/json' },
      body: input.body === undefined ? undefined : JSON.stringify(input.body),
    });
    input.signal?.throwIfAborted();
    return response;
  } catch (error) {
    if (input.signal?.aborted) throw error;
    throw new ApiError(problemFor(0, 'Network Error', 'errors.network'));
  }
};

const canRefresh = (route: RouteDefinition): boolean => route.auth !== 'public';

const isSessionRejection = async (response: Response, signal?: AbortSignal): Promise<boolean> => {
  signal?.throwIfAborted();
  if (response.status !== 401) return false;
  const parsed = problemSchema.safeParse(
    await response
      .clone()
      .json()
      .catch(() => null),
  );
  signal?.throwIfAborted();
  return parsed.success && parsed.data.messageKey === 'errors.unauthorized';
};

const rotateOnce = (): Promise<Response> => {
  refreshInFlight ??= send(authRoutes.refresh, {}).finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
};

const recover = async (
  route: RouteDefinition,
  input: RouteInput<RouteDefinition>,
  initial: Response,
  observedGeneration: number,
  observedSessionGeneration: number,
  lockHeld = false,
): Promise<Response> => {
  assertCurrentSession(observedSessionGeneration);
  const locks = globalThis.navigator?.locks;
  if (!locks) {
    rejectSession();
    return initial;
  }
  const recoverUnderLock = async () => {
    assertCurrentSession(observedSessionGeneration);
    const afterLock = await send(route, input);
    if (!(await isSessionRejection(afterLock, input.signal))) return afterLock;
    if (observedGeneration !== refreshGeneration) {
      rejectSession();
      return afterLock;
    }
    const refreshed = await rotateOnce();
    if (await isSessionRejection(refreshed, input.signal)) {
      rejectSession();
      return refreshed;
    }
    if (!refreshed.ok) return refreshed;
    refreshGeneration += 1;
    const retried = await send(route, input);
    if (await isSessionRejection(retried, input.signal)) rejectSession();
    return retried;
  };
  return lockHeld ? recoverUnderLock() : withSessionLock(recoverUnderLock);
};

const executeRoute = async <R extends RouteDefinition>(
  route: R,
  input: RouteInput<R> = {},
): Promise<{ data: RouteOutput<R>; generation: number }> => {
  const observedGeneration = refreshGeneration;
  const observedSessionGeneration = getSessionGeneration();
  let resultGeneration = observedSessionGeneration;
  let response = changesSession(route)
    ? await withSessionLock(async () => {
        if (deletesAccount(route)) assertCurrentSession(observedSessionGeneration);
        let authentication = await send(route, input);
        if (deletesAccount(route) && (await isSessionRejection(authentication, input.signal))) {
          authentication = await recover(
            route,
            input,
            authentication,
            observedGeneration,
            observedSessionGeneration,
            true,
          );
        }
        if (deletesAccount(route)) assertCurrentSession(observedSessionGeneration);
        if (authentication.ok) resultGeneration = advanceSessionGeneration();
        return authentication;
      })
    : await send(route, input);
  if (
    !changesSession(route) &&
    canRefresh(route) &&
    (await isSessionRejection(response, input.signal))
  ) {
    response = await recover(route, input, response, observedGeneration, observedSessionGeneration);
  }
  if (response.status === 204) {
    if (changesSession(route)) assertCurrentSession(resultGeneration);
    return { data: null as RouteOutput<R>, generation: resultGeneration };
  }
  const payload: unknown = await response.json().catch(() => null);
  input.signal?.throwIfAborted();
  if (response.ok && changesSession(route)) assertCurrentSession(resultGeneration);
  if (!response.ok) {
    const parsed = problemSchema.safeParse(payload);
    throw new ApiError(
      parsed.success
        ? parsed.data
        : problemFor(response.status, response.statusText, 'errors.internal'),
    );
  }
  return {
    data: (import.meta.env.DEV ? route.response.parse(payload) : payload) as RouteOutput<R>,
    generation: resultGeneration,
  };
};

export const callRoute = async <R extends RouteDefinition>(
  route: R,
  input: RouteInput<R> = {},
): Promise<RouteOutput<R>> => (await executeRoute(route, input)).data;

export const callSessionRoute = async <
  R extends typeof authRoutes.login | typeof authRoutes.logout | typeof userRoutes.deleteAccount,
>(
  route: R,
  input: RouteInput<R> = {},
): Promise<{ data: RouteOutput<R>; generation: number }> => executeRoute(route, input);
